import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { plusIcon, rightDirectionBlackIcon } from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import TaskTypeDialog from "../dialogs/TaskTypeDialog";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { componentKey, setCloseMasterDataDialog } from "../SettingsSlice";
import {
  getMasterDataList,
  postTaskType,
  patchTaskType,
  deleteTaskType,
} from "../SettingsSaga";
import moment from "moment";

const TASK_TYPE = "task_type";

const TaskTypeScreen = () => {
  const { masterDataList, closeMasterDataDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deviceType = useDeviceType();

  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [defaultTaskTypeDataRow, setDefaultTaskTypeDataRow] = useState<any>({});

  // OPEN ADD / EDIT TASK TYPE DIALOG
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // TASK TYPE COLUMN DATA
  const TaskTypeColumns = [
    {
      field: "name",
      label: <div>Name</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.name}</p>;
      },
    },
    {
      field: "createdAt",
      label: <div>Created At</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.createdAt
              ? moment(row?.createdAt).format("MM/DD/YYYY HH:mm")
              : "-"}
          </p>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              {
                label: "Edit",
                onClickCb: () => {
                  setDefaultTaskTypeDataRow(row);
                  setDialogState("edit");
                },
              },
              {
                label: "Delete",
                onClickCb: () => {
                  setSelectedId(row?.id);
                  setOpenConfirmationDialog(true);
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  // TASK TYPE LIST ROW DATA
  const taskTypeRowData = masterDataList?.masterData?.map((data: any) => ({
    id: data?.id || "-",
    name: data?.name || "-",
    createdAt: data?.createdAt || "-",
  }));

  useEffect(() => {
    getTaskType();
  }, [rowPerPage, pageNumber]);

  useEffect(() => {
    if (closeMasterDataDialog) {
      dispatch(setCloseMasterDataDialog(false));
      setDialogState(null);
      setDefaultTaskTypeDataRow({});
      setOpenConfirmationDialog(false);
      setSelectedId(null);
      getTaskType();
    }
  }, [closeMasterDataDialog]);

  const getTaskType = () => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      type: TASK_TYPE,
    };
    dispatch(getMasterDataList(payload));
  };

  const addTaskType = (data: any) => {
    try {
      const payload = {
        name: data?.name,
        type: TASK_TYPE,
      };
      if (dialogState === "edit" && defaultTaskTypeDataRow?.id) {
        dispatch(
          patchTaskType({
            id: defaultTaskTypeDataRow.id,
            payload,
          })
        );
      } else {
        dispatch(postTaskType(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title="Task Type" customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Task Type"
              variant="contained"
              onClickCb={() => {
                setDefaultTaskTypeDataRow({});
                setDialogState("add");
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={TaskTypeColumns}
            rows={taskTypeRowData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: masterDataList?.totalPages,
              totalCount: masterDataList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {/* ADD / EDIT TASK TYPE DIALOG */}
      {dialogState && (
        <TaskTypeDialog
          defaultTaskTypeData={defaultTaskTypeDataRow}
          isOpen={!!dialogState}
          handleClose={() => {
            setDialogState(null);
            setDefaultTaskTypeDataRow({});
          }}
          handleData={(data) => {
            addTaskType(data);
          }}
        />
      )}

      {/* DELETE TASK TYPE CONFIRMATION DIALOG */}
      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Task Type"
          alertDescription="Are you sure you want to delete this task type? Please confirm to proceed."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedId(null);
            } else if (action === "submit" && selectedId) {
              dispatch(deleteTaskType({ id: selectedId }));
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default TaskTypeScreen;
